import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: "all", label: "Todo" },
  { id: "image", label: "Flyers" },
  { id: "video", label: "Videos" },
  { id: "sticker", label: "Stickers" },
];

export const MediaGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/media`)
      .then(({ data }) => alive && setItems(data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = items.filter((i) => filter === "all" || i.tipo === filter);

  const close = () => setLightboxIdx(null);
  const next = () => setLightboxIdx((i) => (i + 1) % filtered.length);
  const prev = () => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length);

  return (
    <section id="multimedia" className="bg-white py-24 md:py-32" data-testid="media-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#0A2463]" />
              <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
                Multimedia
              </span>
            </div>
            <h2 className="font-headings font-bold uppercase tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Mirá y <span className="fy-underline">compartí</span><br /> el movimiento
            </h2>
          </div>
          <div className="lg:col-span-6 flex flex-col justify-end gap-5">
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Videos, flyers y campañas gráficas del movimiento. Bajá, compartí, viralizá.
            </p>
            <div className="flex gap-2" data-testid="media-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 text-sm font-headings uppercase tracking-wide border transition-colors ${
                    filter === f.id
                      ? "bg-[#0A2463] text-white border-[#0A2463]"
                      : "border-gray-300 text-[#0A192F] hover:border-[#0A2463]"
                  }`}
                  data-testid={`media-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#9CA3AF] py-16">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-gray-300 p-16 text-center text-[#9CA3AF]" data-testid="media-empty">
            <ImageIcon className="mx-auto mb-3" size={28} />
            <p>Aún no hay contenido multimedia. Volvé pronto.</p>
          </div>
        ) : (
          <>
            {/* Separadores especiales para stickers cuando filtro = all */}
            {filter === "all" && filtered.some((i) => i.tipo === "sticker") && (
              <div className="mb-10" data-testid="stickers-row">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-8 bg-[#F5B400]" />
                  <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
                    Stickers — Descargá y pegá
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                  {filtered.filter((i) => i.tipo === "sticker").map((m, idx) => (
                    <StickerCard key={m.id} item={m} onClick={() => setLightboxIdx(filtered.indexOf(m))} index={idx} />
                  ))}
                </div>
              </div>
            )}

            {filter === "sticker" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="media-grid">
                {filtered.map((m, idx) => (
                  <StickerCard key={m.id} item={m} onClick={() => setLightboxIdx(idx)} index={idx} large />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="media-grid">
                {filtered.filter((m) => filter !== "all" || m.tipo !== "sticker").map((m, idx) => {
                  const actualIdx = filtered.indexOf(m);
                  return (
                    <motion.button
                      key={m.id}
                      onClick={() => setLightboxIdx(actualIdx)}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.4, delay: (idx % 6) * 0.04 }}
                      className="group relative overflow-hidden bg-[#0A192F] aspect-video text-left"
                      data-testid={`media-item-${m.id}`}
                    >
                      {m.tipo === "image" ? (
                        <img
                          src={m.data_url}
                          alt={m.titulo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0A2463] to-[#0A192F]">
                          <div className="w-16 h-16 rounded-full bg-[#F5B400] flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play size={24} className="text-[#0A192F] ml-1" fill="currentColor" />
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      <div className="absolute left-4 right-4 bottom-3 text-white">
                        <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#F5B400] font-headings mb-1">
                          {m.tipo === "video" ? "Video" : "Imagen"}
                        </span>
                        <div className="font-headings text-base font-semibold leading-snug line-clamp-2">
                          {m.titulo}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && filtered[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-8"
            onClick={close}
            data-testid="media-lightbox"
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
              aria-label="Cerrar"
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            {filtered.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-4 text-white/80 hover:text-white"
                  aria-label="Anterior"
                >
                  <ChevronLeft size={40} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-4 text-white/80 hover:text-white"
                  aria-label="Siguiente"
                >
                  <ChevronRight size={40} />
                </button>
              </>
            )}
            <div
              className="max-w-5xl w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              {filtered[lightboxIdx].tipo === "image" ? (
                <img
                  src={filtered[lightboxIdx].data_url}
                  alt={filtered[lightboxIdx].titulo}
                  className="max-w-full max-h-[80vh] mx-auto object-contain"
                />
              ) : (filtered[lightboxIdx].video_url || "").startsWith("data:video/") || /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(filtered[lightboxIdx].video_url || "") ? (
                <video
                  src={filtered[lightboxIdx].video_url}
                  controls
                  autoPlay
                  playsInline
                  className="max-w-full max-h-[80vh] mx-auto block bg-black"
                  data-testid="lightbox-video-file"
                />
              ) : (
                <div className="relative aspect-video w-full">
                  <iframe
                    src={filtered[lightboxIdx].video_url}
                    title={filtered[lightboxIdx].titulo}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="mt-5 text-center text-white">
                <h3 className="font-headings text-xl uppercase tracking-tight">{filtered[lightboxIdx].titulo}</h3>
                {filtered[lightboxIdx].descripcion && (
                  <p className="text-white/70 text-sm mt-2 max-w-2xl mx-auto">
                    {filtered[lightboxIdx].descripcion}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default MediaGallery;

const StickerCard = ({ item, onClick, index = 0, large = false }) => {
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.85, rotate: -2 }}
      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.05 }}
      whileHover={{ rotate: 2, scale: 1.04 }}
      className={`group relative bg-white rounded-2xl shadow-lg shadow-black/10 hover:shadow-2xl transition-shadow ${
        large ? "p-6 aspect-square" : "p-4 aspect-square"
      }`}
      style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)" }}
      data-testid={`sticker-${item.id}`}
    >
      <div className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.2em] bg-[#F5B400] text-[#0A192F] px-2 py-0.5 font-headings z-10">
        Sticker
      </div>
      <img
        src={item.data_url}
        alt={item.titulo}
        className="w-full h-full object-contain"
        style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.12))" }}
      />
      {large && (
        <div className="absolute inset-x-0 bottom-0 p-3 text-center font-headings text-xs uppercase tracking-wide text-[#0A192F] truncate">
          {item.titulo}
        </div>
      )}
    </motion.button>
  );
};
